import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { CellGrid } from './SolarShapes';

interface RevealOverlayProps {
  /** Radius of the revealed area, in pixels. */
  radius?: number;
  /** Stroke colour of the schematic. Defaults to white for photo bands. */
  color?: string;
}

/**
 * Cursor-revealed schematic drawn over a photographic band.
 *
 * Scoped to its own section rather than the viewport: it listens on its parent
 * and only while the pointer is inside it, so no work happens for the rest of
 * the page. Coordinates are parent-relative, which keeps the mask aligned no
 * matter where the band sits in the document or how far it has scrolled.
 *
 * It cannot affect content: `pointer-events: none` so clicks and text
 * selection pass through, `aria-hidden` so assistive tech skips it, absolutely
 * positioned so it can never shift layout, and it sits below the content layer.
 */
export function RevealOverlay({ radius = 300, color = 'rgba(255,255,255,0.5)' }: RevealOverlayProps) {
  const ref = useRef<HTMLDivElement>(null);
  const frame = useRef<number>();
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    // Listen on the whole band, not the immediate parent: the hero's text sits
    // in a sibling subtree, so a parent-only listener never sees the pointer.
    const host = el?.closest('section') ?? el?.parentElement;
    if (!el || !host) return;

    // Touch devices have no cursor to reveal with, and a reduced-motion
    // preference rules out a cursor-tracked effect entirely.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const noHover = window.matchMedia?.('(hover: none)').matches;
    if (still || noHover) return;

    const onMove = (event: PointerEvent) => {
      if (frame.current) cancelAnimationFrame(frame.current);
      // One update per frame: pointermove fires far more often than that.
      frame.current = requestAnimationFrame(() => {
        // Coordinates relative to the painted layer, which is what the mask
        // is positioned against.
        const r = el.getBoundingClientRect();
        setPos({
          x: ((event.clientX - r.left) / r.width) * 100,
          y: ((event.clientY - r.top) / r.height) * 100,
        });
      });
    };
    const enter = () => setActive(true);
    const leave = () => {
      setActive(false);
      if (frame.current) cancelAnimationFrame(frame.current);
    };

    host.addEventListener('pointermove', onMove, { passive: true });
    host.addEventListener('pointerenter', enter);
    host.addEventListener('pointerleave', leave);
    return () => {
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerenter', enter);
      host.removeEventListener('pointerleave', leave);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, []);

  const mask = `radial-gradient(${radius}px circle at ${pos.x}% ${pos.y}%, #000 0%, #000 30%, transparent 72%)`;

  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        contain: 'layout paint',
        color,
        opacity: active ? 1 : 0,
        transition: 'opacity .35s ease',
        maskImage: mask,
        WebkitMaskImage: mask,
      }}
    >
      <CellGrid />
    </Box>
  );
}
